import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Avatar, Divider, Skeleton } from "@mui/material";
import {
  PeopleAltOutlined,
  EventNoteOutlined,
  ReceiptLongOutlined,
  PersonOutlined,
  CalendarTodayOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import { CiLogout } from "react-icons/ci";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import theme, {
  formatThemeForReservedArea,
} from "../../../config/MuiColorThemes";
import BookingApi from "../../../API/Apis/BookingApi";
import { t } from "i18next";

const USER_CACHE_KEY = "cachedUserProfile";

function BookingDetailsListSideNavigation() {
  const navigateTo = useNavigate();
  const location = useLocation();

  // Dropdown open state — auto-opens when on a profile route
  const profileRoutes = ["/profile", "/changePassword"];
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(
    profileRoutes.includes(location.pathname)
  );

  // Keep dropdown open whenever user navigates to a profile sub-page
  useEffect(() => {
    if (profileRoutes.includes(location.pathname)) {
      setIsDropdownOpen(true);
    }
  }, [location.pathname]);

  const [userData, setUserData] = useState<any>(() => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  const [userLoading, setUserLoading] = useState<boolean>(
    !localStorage.getItem(USER_CACHE_KEY)
  );

  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      const newTheme = createTheme(dynamicPalette);
      setDynamicTheme(newTheme);
    }
    fetchTheme();
  }, []);

  const id = localStorage.getItem("userID");

  const getUserData = async (): Promise<void> => {
    try {
      if (!localStorage.getItem(USER_CACHE_KEY)) {
        setUserLoading(true);
      }
      if (id) {
        const response = await BookingApi.getUserProfileByUserId(Number(id));
        const result = response?.data;
        if (result?.onlineUser?.length > 0) {
          const user = result.onlineUser[0];
          setUserData(user);
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  const getImageSrc = (img: string): string | null => {
    if (!img) return null;
    return img.startsWith("data:image") ? img : `data:image/png;base64,${img}`;
  };

  const handleLogout = (): void => {
    localStorage.setItem("isUserLoggedin", "false");
    localStorage.removeItem("userID");
    localStorage.removeItem("customerId");
    localStorage.removeItem(USER_CACHE_KEY); // clear cached profile on logout
    navigateTo("/signin");
  };

  const hasProfileImage = Boolean(userData?.profile_picture_base64);
  const primaryColor = dynamicTheme?.palette?.primary?.main || "#2cb133";

  const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "15px",
    color: isActive ? primaryColor : "#333",
    borderLeft: isActive
      ? `4px solid ${primaryColor}`
      : "4px solid transparent",
    fontWeight: isActive ? 600 : 400,
    backgroundColor: isActive ? `${primaryColor}18` : "transparent",
    transition: "background 0.15s",
  });

  const subLinkStyle = (isActive: boolean): React.CSSProperties => ({
    display: "block",
    fontSize: "14px",
    color: isActive ? "#000000" : "#555",
    textDecoration: "none",
    fontWeight: isActive ? 600 : 400,
    padding: "2px 0",
  });
  const LogoutIcon = CiLogout as React.ElementType;

  return (
    <ThemeProvider theme={dynamicTheme || theme}>
      <div
        style={{
          minHeight: "100%",
          width: "260px",
          backgroundColor: "#f8f9fa",
          boxShadow: "2px 0 8px rgba(0,0,0,0.08)",
          padding: "32px 16px",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflowY: "auto",
        }}
      >
        {/* User info */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginBottom: "24px",
          }}
        >
          <Avatar
            alt={userData?.firstName?.[0]}
            src={
              hasProfileImage
                ? getImageSrc(userData.profile_picture_base64)
                : ""
            }
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#9ca3af",
              fontSize: "20px",
              fontWeight: 400,
            }}
          >
            {!hasProfileImage &&
              `${userData?.firstName?.[0] || ""}${
                userData?.lastName?.[0] || ""
              }`.toUpperCase()}
          </Avatar>
          {userLoading ? (
            <>
              <Skeleton
                variant="text"
                width={140}
                height={22}
                style={{ marginTop: "10px", borderRadius: "6px" }}
              />
              <Skeleton
                variant="text"
                width={110}
                height={16}
                style={{ marginTop: "2px", borderRadius: "6px" }}
              />
            </>
          ) : (
            <>
              <p
                style={{
                  fontWeight: 600,
                  fontSize: "15px",
                  marginTop: "10px",
                  marginBottom: "2px",
                  color: "#111",
                }}
              >
                {`${userData?.firstName || ""} ${
                  userData?.lastName || ""
                }`.trim()}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9ca3af",
                  margin: 0,
                }}
              >
                {userData?.email || ""}
              </p>
            </>
          )}
        </div>

        <Divider style={{ marginBottom: "16px" }} />

        {/* Main navigation */}
        <nav style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <NavLink
            to="/customerDetailsList"
            style={({ isActive }) => navLinkStyle(isActive)}
          >
            <PeopleAltOutlined style={{ fontSize: "20px" }} />
            {t("Customer Details")}
          </NavLink>

          <NavLink to="/" end style={({ isActive }) => navLinkStyle(isActive)}>
            <CalendarTodayOutlined style={{ fontSize: "20px" }} />
            {t("CreateBooking")}
          </NavLink>

          <NavLink
            to="/myReservedBookings"
            style={({ isActive }) => navLinkStyle(isActive)}
          >
            <EventNoteOutlined style={{ fontSize: "20px" }} />
            {t("List of reservation")}
          </NavLink>

          <NavLink
            to="/myInvoices"
            style={({ isActive }) => navLinkStyle(isActive)}
          >
            <ReceiptLongOutlined style={{ fontSize: "20px" }} />
            {t("List of invoice")}
          </NavLink>
        </nav>

        <Divider style={{ margin: "30px 0" }} />

        <div>
          {/* My Profile — clickable dropdown header */}

          {(() => {
            const isProfileActive = profileRoutes.includes(location.pathname);
            return (
              <div
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  padding: "10px 12px",
                  fontSize: "15px",
                  color: isProfileActive ? primaryColor : "#333",
                  fontWeight: isProfileActive ? 600 : 450,
                  cursor: "pointer",
                  borderRadius: "6px",
                  borderLeft: isProfileActive
                    ? `4px solid ${primaryColor}`
                    : "4px solid transparent",
                  backgroundColor: isProfileActive
                    ? `${primaryColor}18`
                    : "transparent",
                  transition: "background 0.15s",
                }}
              >
                <span
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <PersonOutlined style={{ fontSize: "20px" }} />
                  {t("My Profile")}
                </span>
                {/* Chevron rotates when open */}
                <ExpandMoreOutlined
                  style={{
                    fontSize: "18px",
                    transition: "transform 0.2s ease",
                    transform: isDropdownOpen
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </div>
            );
          })()}

          {/* Sub-items — shown only when dropdown is open */}
          {isDropdownOpen && (
            <div
              style={{
                paddingLeft: "42px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginTop: "4px",
                marginBottom: "4px",
              }}
            >
              <NavLink
                to="/profile"
                style={({ isActive }) => subLinkStyle(isActive)}
              >
                • {t("Personal information")}
              </NavLink>
              <NavLink
                to="/changePassword"
                style={({ isActive }) => subLinkStyle(isActive)}
              >
                • {t("Password")}
              </NavLink>
            </div>
          )}
        </div>

        <Divider style={{ margin: "30px 0" }} />

        {/* Log out */}
        <div
          onClick={handleLogout}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 12px",
            cursor: "pointer",
            fontSize: "15px",
            color: "#333",
            borderRadius: "6px",
            borderLeft: "4px solid transparent",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f3f4f6")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        >
          <LogoutIcon style={{ fontSize: "20px" }} />

          {t("Logout")}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default BookingDetailsListSideNavigation;
